import SortIcon from '@mui/icons-material/Sort';
import { Button, Divider, MenuItem, Popover, useTheme } from '@mui/material';
import { Fragment, memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { bloxValues } from 'themes/constants';
import { useSearchModalContext } from '../SearchModal';

/**
 * --------------------------------------------------------
 * Generic Sort Dropdown Component
 * - Generally used on card views
 * --------------------------------------------------------
 */

export const SearchModalSortDropdownGeneric = memo(() => {
  const { filters, setFilters, sortDirConfig, sortOptions } = useSearchModalContext();

  const sortKey = sortDirConfig?.sortKey;
  const dirKey = sortDirConfig?.dirKey;
  const uppercaseDir = sortDirConfig?.uppercaseDir;

  const theme = useTheme();
  const { t } = useTranslation();

  const strSort = t('blox.search.sort.label');
  const strRemoveSort = t('blox.search.sort.remove_sort');
  const strAsc = t('blox.search.sort.ascending');
  const strDesc = t('blox.search.sort.descending');

  // Popover state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'sort-popover' : undefined;

  // Normalized dir and field from filters
  const currentField = filters?.[sortKey];
  const rawDir = filters?.[dirKey];
  const currentDir = uppercaseDir ? rawDir?.toUpperCase() : rawDir?.toLowerCase();
  const currentOption = sortOptions.find((option) => option.field === currentField);

  const normalizedAsc = uppercaseDir ? 'ASC' : 'asc';
  const normalizedDesc = uppercaseDir ? 'DESC' : 'desc';

  const sortButtonText =
    currentField && currentOption
      ? t('blox.search.sort.sorted_label_dir', {
          label: currentOption.label,
          direction: currentDir === normalizedAsc ? strAsc : strDesc,
        })
      : strSort;

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSortChange = useCallback(
    (field: string, direction: string) => {
      const normalizedDir = uppercaseDir
        ? direction.toUpperCase()
        : direction.toLowerCase();
      setFilters((prev) => ({
        ...prev,
        [sortKey]: field,
        [dirKey]: normalizedDir,
      }));
    },
    [setFilters, sortKey, dirKey, uppercaseDir],
  );

  const handleClearSort = () => {
    setFilters((prev) => {
      const updated = { ...prev };
      delete updated[sortKey];
      delete updated[dirKey];
      return updated;
    });
    handleClose();
  };

  return (
    <Fragment>
      <Button
        data-testid="search-modal-sort-button"
        aria-describedby={id}
        onClick={handleClick}
        color="primary"
        size="small"
        sx={{
          fontSize: bloxValues.smallFontSizeRem,
          px: '4px',
          py: '2px',
          '& .MuiButton-icon': {
            m: 0,
            ml: 0.5,
          },
        }}
        endIcon={
          <SortIcon
            sx={{
              transition: 'transform 0.3s',
              transform:
                currentDir === normalizedAsc ? 'rotateX(180deg)' : 'rotateX(0deg)',
              color: theme.palette.primary.main,
              fontSize: 'inherit',
            }}
          />
        }
      >
        {sortButtonText}
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        elevation={3}
        marginThreshold={2}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        {sortOptions.map((option) => (
          <Fragment key={option?.field}>
            {/* Menu Item for Ascending */}
            <MenuItem
              data-testid={`sort-option-${option?.field}-asc`}
              onClick={() => handleSortChange(option?.field, normalizedAsc)}
              selected={currentField === option?.field && currentDir === normalizedAsc}
            >
              {`${option?.label} - ${strAsc}`}
            </MenuItem>
            {/* Menu Item for Descending */}
            <MenuItem
              data-testid={`sort-option-${option?.field}-desc`}
              onClick={() => handleSortChange(option?.field, normalizedDesc)}
              selected={currentField === option?.field && currentDir === normalizedDesc}
            >
              {`${option?.label} - ${strDesc}`}
            </MenuItem>
          </Fragment>
        ))}

        {currentField && (
          <Fragment>
            <Divider />
            <MenuItem
              data-testid="sort-option-remove-sort"
              onClick={handleClearSort}
            >
              {strRemoveSort}
            </MenuItem>
          </Fragment>
        )}
      </Popover>
    </Fragment>
  );
});
SearchModalSortDropdownGeneric.displayName = 'SearchModalSortDropdownGeneric';
